import { SPOTLIGHT_BORDER } from './tourStyles';

// Reactour step-style helpers shared across more than one tour. Tour-specific
// helpers (e.g. the various *DrawerObservers) live in their own tour file.

// Full click block with NO dimming — nothing on the page is operable, only the
// popover (Next / Skip / Back). Works for a 'body'-anchored intro AND for a step
// anchored to a specific control: the click-catching `clickArea` covers the page
// EXCEPT the target's hole, and this invisible, pointerEvents:auto highlightedArea
// covers that hole too — so even the pointed-at control can't be clicked. (Note:
// overriding `maskArea` size does NOT block the target — reactour builds the
// clickArea hole from the measured target rect, not from the maskArea style.) The
// opacity-0 mask keeps it all invisible. For "here's what you CAN do" info steps.
export const blockAllNoDim = {
	maskWrapper: (base: Record<string, unknown>) => ({ ...base, opacity: 0 }),
	highlightedArea: (base: Record<string, unknown>) => ({
		...base,
		display: 'block',
		pointerEvents: 'auto' as const,
		// The target is blocked too here, so flag it with the "no" cursor.
		cursor: 'not-allowed',
	}),
};

// Keeps the spotlight ring but makes the highlighted element NON-interactive:
// a transparent, click-catching rect (pointerEvents:auto) sits over the target,
// so the user can see it and read about it but can't actually click it. Use for
// "just so you know" info steps where letting the user act would break the tour
// flow (e.g. a toggle that reveals extra fields). Overrides the global
// highlightedArea, which is click-through by default.
export const blockInteraction = {
	highlightedArea: (base: Record<string, unknown>) => ({
		...base,
		display: 'block',
		pointerEvents: 'auto' as const,
		cursor: 'not-allowed',
		fill: 'transparent',
		stroke: SPOTLIGHT_BORDER.color,
		strokeWidth: SPOTLIGHT_BORDER.width,
	}),
};

// Step config for highlighting a sidebar / CANtabs menu target. The sidebar
// animates open (57px -> 15rem), so we watch the target and re-measure once it
// resizes — otherwise the popover stays pinned to the collapsed rail and can end
// up sitting on top of the expanded menu. The rows/sidebar are full-width, so we
// also zero the horizontal mask padding ([0, 6] is read as [horizontal,
// vertical]) to stop the spotlight ring spilling past the sidebar edge into the
// page. Spread into a step; combine with `styles: blockInteraction` to also stop
// the highlighted menu from being clicked.
export const sidebarMenuStep = (selector: string) => ({
	padding: { mask: [0, 6], popover: 10 },
	mutationObservables: [selector],
	resizeObservables: [selector],
});

// A fully non-blocking, invisible mask. reactour's dark overlay captures clicks
// via `clickArea` (pointerEvents:auto by default), NOT `maskWrapper` — so we
// must null `clickArea` to let clicks reach the page, and drop the dim to 0 so
// nothing is greyed out.
export const clickThroughMask = {
	maskWrapper: (base: Record<string, unknown>) => ({ ...base, opacity: 0 }),
	clickArea: (base: Record<string, unknown>) => ({
		...base,
		pointerEvents: 'none' as const,
	}),
};

// No dim, and ONLY the highlighted target stays clickable. The mask keeps its
// normal cut-out at the target, so clicks there reach the page (e.g. Save) while
// the click-catching `clickArea` still blocks the rest — but nothing is greyed
// out, so the user can read the whole screen. For a review/confirm step: look
// around freely, but the only control you can press is the one we point at.
export const onlyTargetNoDim = {
	maskWrapper: (base: Record<string, unknown>) => ({ ...base, opacity: 0 }),
};

// Greys out the ENTIRE screen (no spotlight cut-out) so an info callout floats
// over a fully-dimmed page. Achieved by collapsing the mask's highlight rect to
// zero size, leaving the dark overlay whole.
export const fullGrayMask = {
	maskArea: (base: Record<string, unknown>) => ({
		...base,
		width: 0,
		height: 0,
	}),
	// Whole-page dim with no real target — suppress the spotlight ring.
	highlightedArea: () => ({ display: 'none' }),
};

// Pins a 'body'-anchored popover to the very top-centre of the screen so it
// never covers the centred camera / upload dialogs it floats over.
export const topCenterPosition = (
	{ right }: { right: number },
	prevRect: { width: number },
): [number, number] => [Math.max(8, (right - (prevRect?.width ?? 300)) / 2), 12];
